import { useMutation, useQuery } from "@apollo/client";
import { Box, Divider, FormControlLabel, Stack, Switch, Typography } from "@mui/material";
import {
  FEATURE_FLAG_DEFS,
  KNOWN_FEATURE_FLAG_KEYS,
  type FeatureFlagMap,
} from "encamp-shared/src/featureFlags";
import { useMemo } from "react";

import { gql } from "../../../../../generated-graphql";

const TENANT_FLAGS_QUERY = gql(`
  query TenantFeatureFlags($tenantId: ID!) {
    tenant(id: $tenantId) {
      id
      name
      tenantConfigs {
        id
        tenantId
        key
        value
      }
    }
  }
`);

const SET_FLAG_MUTATION = gql(`
  mutation SetTenantFeatureFlag($tenantId: ID!, $key: String!, $value: Boolean!) {
    setTenantFeatureFlag(tenantId: $tenantId, key: $key, value: $value) {
      id
      tenantConfigs {
        id
        tenantId
        key
        value
      }
    }
  }
`);

export function FeatureFlagsTab({ tenantId }: { tenantId: string }) {
  const { data, loading } = useQuery(TENANT_FLAGS_QUERY, {
    variables: { tenantId },
    fetchPolicy: "cache-and-network",
  });
  const [setFlag] = useMutation(SET_FLAG_MUTATION);

  const featureFlags = useMemo(() => {
    const trueFeatureFlags = new Set(
      (data?.tenant?.tenantConfigs ?? [])
        .filter((config) => config.value)
        .map((config) => config.key)
    );

    const featureFlags: Partial<FeatureFlagMap> = {};
    for (const key of KNOWN_FEATURE_FLAG_KEYS) {
      featureFlags[key] = trueFeatureFlags.has(key);
    }
    return featureFlags;
  }, [data?.tenant?.tenantConfigs]);

  const handleToggle = (flagKey: string) => async (_event: unknown, checked: boolean) => {
    const optimisticTenantConfigs =
      data?.tenant?.tenantConfigs.filter((config) => config.key !== flagKey) ?? [];
    optimisticTenantConfigs.push({
      __typename: "TenantConfig",
      // apollo cleans up optimistic responses after mutation completes, so we don't need the real id here,
      // but we use one that will be unique to this flag anyway
      id: `temp-${tenantId}-${flagKey}`,
      tenantId,
      key: flagKey,
      value: checked,
    });

    await setFlag({
      variables: { tenantId, key: flagKey, value: checked },
      optimisticResponse: {
        setTenantFeatureFlag: {
          __typename: "Tenant",
          id: tenantId,
          tenantConfigs: optimisticTenantConfigs,
        },
      },
    });
  };

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="h6">Feature flags</Typography>
        <Typography variant="body2" color="text.secondary">
          Toggle Encamp-managed feature flags for this organization. These override or replace
          LaunchDarkly settings over time.
        </Typography>
      </Box>
      <Divider />
      <Stack spacing={2}>
        {FEATURE_FLAG_DEFS.map((f) => (
          <Box key={f.key}>
            <FormControlLabel
              disabled={loading}
              control={
                <Switch checked={Boolean(featureFlags[f.key])} onChange={handleToggle(f.key)} />
              }
              label={
                <Stack>
                  <Typography variant="body1">{f.label}</Typography>
                  {f.description && (
                    <Typography variant="caption" color="text.secondary">
                      {f.description}
                    </Typography>
                  )}
                </Stack>
              }
            />
          </Box>
        ))}
      </Stack>
    </Stack>
  );
}
